"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { toast } from "sonner";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentEmail: string;
}

type Step = "confirm" | "verify" | "compose" | "done";

const RESEND_SECONDS = 60;
// Demo verification code accepted by the modal — anything else triggers the
// error toast and a re-entry prompt.
const ACCEPT_CODE = "123456";

export function ChangeEmailModal({
  open,
  onOpenChange,
  currentEmail,
}: Props) {
  const [step, setStep] = React.useState<Step>("confirm");
  const [code, setCode] = React.useState("");
  const [newEmail, setNewEmail] = React.useState("");
  const [resendIn, setResendIn] = React.useState(RESEND_SECONDS);

  // Reset modal state every time it opens.
  React.useEffect(() => {
    if (open) {
      setStep("confirm");
      setCode("");
      setNewEmail("");
      setResendIn(RESEND_SECONDS);
    }
  }, [open]);

  // Resend cooldown timer — only ticks while the verify step is showing.
  React.useEffect(() => {
    if (step !== "verify" || resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [step, resendIn]);

  const startVerify = () => {
    setStep("verify");
    setCode("");
    setResendIn(RESEND_SECONDS);
    toast(`驗證碼已發送至 ${currentEmail}`, { duration: 2200 });
  };

  const submitCode = () => {
    if (code.trim() === ACCEPT_CODE) {
      setStep("compose");
    } else {
      toast.error("驗證碼不正確，請重新輸入。", { duration: 2200 });
      setCode("");
    }
  };

  const resend = () => {
    if (resendIn > 0) return;
    setResendIn(RESEND_SECONDS);
    toast(`已重新發送驗證碼至 ${currentEmail}`, { duration: 2000 });
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 flex w-[calc(100vw-32px)] max-w-[440px] -translate-x-1/2 -translate-y-1/2 flex-col gap-[20px] bg-white p-[32px] shadow-xl"
          style={{ borderRadius: 3 }}
        >
          <DialogPrimitive.Title
            style={{
              fontFamily: CORE_FONT.condensed,
              fontSize: 24,
              fontWeight: 700,
              color: CORE_COLORS.textPrimary,
              lineHeight: 1.1,
              letterSpacing: "-0.2px",
            }}
          >
            更改電郵
          </DialogPrimitive.Title>

          {step === "confirm" && (
            <ConfirmStep
              currentEmail={currentEmail}
              onCancel={() => onOpenChange(false)}
              onContinue={startVerify}
            />
          )}
          {step === "verify" && (
            <VerifyStep
              currentEmail={currentEmail}
              code={code}
              onCodeChange={setCode}
              onSubmit={submitCode}
              resendIn={resendIn}
              onResend={resend}
            />
          )}
          {step === "compose" && (
            <ComposeStep
              currentEmail={currentEmail}
              value={newEmail}
              onChange={setNewEmail}
              onConfirm={() => {
                toast(`電郵已更新為 ${newEmail}`, { duration: 2400 });
                setStep("done");
                onOpenChange(false);
              }}
            />
          )}

          <DialogPrimitive.Close
            aria-label="關閉"
            className="absolute right-[16px] top-[16px] inline-flex size-[28px] items-center justify-center transition-colors hover:bg-black/[0.05]"
            style={{
              border: `1px solid rgba(0,0,0,0.15)`,
              borderRadius: "999px",
            }}
          >
            <X
              className="size-[14px]"
              strokeWidth={2}
              color={CORE_COLORS.textPrimary}
            />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function ConfirmStep({
  currentEmail,
  onCancel,
  onContinue,
}: {
  currentEmail: string;
  onCancel: () => void;
  onContinue: () => void;
}) {
  return (
    <>
      <p
        style={{
          fontFamily: CORE_FONT.sans,
          fontSize: 14,
          color: CORE_COLORS.textSecondary,
          lineHeight: 1.6,
        }}
      >
        為保障您的帳戶安全，繼續前我們會將一組 6
        位數字驗證碼寄送至您現時登入的電郵
        <span
          style={{
            color: CORE_COLORS.textPrimary,
            fontWeight: 700,
            margin: "0 4px",
          }}
        >
          {currentEmail}
        </span>
        。請確認是否繼續。
      </p>
      <div className="flex justify-end gap-[8px] pt-[4px]">
        <button
          type="button"
          onClick={onCancel}
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: 14,
            fontWeight: 700,
            color: CORE_COLORS.textPrimary,
            lineHeight: "20px",
            padding: "10px 18px",
            border: `1px solid rgba(0,0,0,0.3)`,
            borderRadius: 3,
            backgroundColor: "white",
          }}
        >
          取消
        </button>
        <button
          type="button"
          onClick={onContinue}
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: 14,
            fontWeight: 700,
            color: "white",
            lineHeight: "20px",
            padding: "10px 18px",
            backgroundColor: CORE_COLORS.brandGreen,
            borderRadius: 3,
            border: "none",
          }}
        >
          發送驗證碼
        </button>
      </div>
    </>
  );
}

function VerifyStep({
  currentEmail,
  code,
  onCodeChange,
  onSubmit,
  resendIn,
  onResend,
}: {
  currentEmail: string;
  code: string;
  onCodeChange: (v: string) => void;
  onSubmit: () => void;
  resendIn: number;
  onResend: () => void;
}) {
  return (
    <>
      <p
        style={{
          fontFamily: CORE_FONT.sans,
          fontSize: 14,
          color: CORE_COLORS.textSecondary,
          lineHeight: 1.6,
        }}
      >
        我們已將驗證碼發送至{" "}
        <span style={{ color: CORE_COLORS.textPrimary, fontWeight: 700 }}>
          {currentEmail}
        </span>
        ，請輸入收到的 6 位數字驗證碼。
      </p>
      <div className="flex flex-col gap-[8px]">
        <label
          htmlFor="v2-change-email-code"
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: 13,
            fontWeight: 700,
            color: CORE_COLORS.textPrimary,
            lineHeight: "18px",
            letterSpacing: "0.2px",
          }}
        >
          驗證碼
        </label>
        <input
          id="v2-change-email-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="輸入 6 位數字"
          value={code}
          onChange={(e) =>
            onCodeChange(e.target.value.replace(/\D/g, "").slice(0, 6))
          }
          className="bg-white outline-none focus-visible:border-[color:var(--green)]"
          style={
            {
              fontFamily: CORE_FONT.sans,
              fontSize: 17,
              color: CORE_COLORS.textPrimary,
              lineHeight: "22px",
              padding: "12px 14px",
              height: 48,
              border: `1px solid rgba(0,0,0,0.3)`,
              borderRadius: 3,
              ["--green" as string]: CORE_COLORS.brandGreen,
            } as React.CSSProperties
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onResend}
          disabled={resendIn > 0}
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: 13,
            fontWeight: 600,
            color: resendIn > 0 ? CORE_COLORS.textTertiary : CORE_COLORS.brandGreen,
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: resendIn > 0 ? "not-allowed" : "pointer",
          }}
        >
          {resendIn > 0 ? `${resendIn} 秒後可重新發送` : "重新發送驗證碼"}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={code.length !== 6}
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: 14,
            fontWeight: 700,
            color: "white",
            lineHeight: "20px",
            padding: "10px 18px",
            backgroundColor: CORE_COLORS.brandGreen,
            borderRadius: 3,
            border: "none",
            opacity: code.length === 6 ? 1 : 0.5,
            cursor: code.length === 6 ? "pointer" : "not-allowed",
          }}
        >
          驗證
        </button>
      </div>
    </>
  );
}

function ComposeStep({
  currentEmail,
  value,
  onChange,
  onConfirm,
}: {
  currentEmail: string;
  value: string;
  onChange: (v: string) => void;
  onConfirm: () => void;
}) {
  return (
    <>
      <div className="flex flex-col gap-[6px]">
        <span
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: 13,
            fontWeight: 700,
            color: CORE_COLORS.textPrimary,
            lineHeight: "18px",
            letterSpacing: "0.2px",
          }}
        >
          現有電郵
        </span>
        <span
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: 15,
            color: CORE_COLORS.textSecondary,
            lineHeight: "22px",
          }}
        >
          {currentEmail}
        </span>
      </div>
      <div className="flex flex-col gap-[8px]">
        <label
          htmlFor="v2-account-new-email"
          style={{
            fontFamily: CORE_FONT.sans,
            fontSize: 13,
            fontWeight: 700,
            color: CORE_COLORS.textPrimary,
            lineHeight: "18px",
            letterSpacing: "0.2px",
          }}
        >
          新電郵
        </label>
        <input
          id="v2-account-new-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder="輸入新電郵"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-white outline-none focus-visible:border-[color:var(--green)]"
          style={
            {
              fontFamily: CORE_FONT.sans,
              fontSize: 15,
              color: CORE_COLORS.textPrimary,
              lineHeight: "22px",
              padding: "12px 14px",
              height: 48,
              border: `1px solid rgba(0,0,0,0.3)`,
              borderRadius: 3,
              ["--green" as string]: CORE_COLORS.brandGreen,
            } as React.CSSProperties
          }
        />
      </div>
      <button
        type="button"
        onClick={onConfirm}
        disabled={value.trim().length === 0}
        className="inline-flex w-full items-center justify-center transition-colors hover:bg-[color:var(--green-dark)] disabled:cursor-not-allowed disabled:opacity-50"
        style={
          {
            fontFamily: CORE_FONT.sans,
            fontSize: 15,
            fontWeight: 700,
            color: "white",
            lineHeight: "20px",
            padding: "14px 20px",
            backgroundColor: CORE_COLORS.brandGreen,
            borderRadius: 3,
            ["--green-dark" as string]: CORE_COLORS.brandGreenDark,
          } as React.CSSProperties
        }
      >
        確認更改
      </button>
    </>
  );
}
